"use client";

import { cn } from "@/lib/utils";

export function Panel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border border-line bg-card shadow-[var(--shadow-card)]",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function PanelHeader({
  title,
  icon,
  hint,
  action,
  className,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <h2 className="truncate text-sm font-semibold tracking-tight">
          {title}
        </h2>
        {hint}
      </div>
      {action}
    </div>
  );
}

export function Chip({
  children,
  tone = "neutral",
  className,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "primary" | "success" | "warning" | "danger" | "purple";
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: "bg-scrim text-text-secondary border-line",
    primary: "bg-primary-soft text-primary border-primary/25",
    success: "bg-success-soft text-success border-success/25",
    warning: "bg-warning-soft text-warning border-warning/25",
    danger: "bg-danger-soft text-danger border-danger/25",
    purple: "bg-purple-soft text-purple border-purple/25",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Meter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "warning" | "danger" | "primary";
}) {
  const bar: Record<string, string> = {
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    primary: "bg-primary",
  };
  return (
    <div className="flex items-center gap-3">
      <span className="w-[68px] shrink-0 text-xs text-text-secondary">
        {label}
      </span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-track">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-700 ease-out",
            bar[tone],
          )}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="tnum w-9 shrink-0 text-right text-xs font-semibold">
        {value}%
      </span>
    </div>
  );
}

export function toneForScore(value: number) {
  if (value >= 80) return "success" as const;
  if (value >= 65) return "primary" as const;
  if (value >= 45) return "warning" as const;
  return "danger" as const;
}
